import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { CheckCircle, InfoOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Grid2 as Grid,
  List,
  ListItem,
  Paper,
  Typography,
} from '@mui/material'
import { useCustomerApplications } from '@/api/financial/customer/list-customer-applications'
import { useReadUserProfile } from '@/api/user-manager'
import { PageHeader } from '@/components/page-header'
import { productPromoLinks } from '@/constants/product-promo'
import { routes } from '@/routes'
import { creditProducts, isCreditShowcaseProductKey } from './credit-products'
import { lockOf, productsFor, profileOf } from './credit-profile'
import { useStartApplication } from './start-application'

export function ProductDetailPage() {
  const { product: productParam } = useParams()
  const navigate = useNavigate()
  const { data: user, isLoading: profileLoading } = useReadUserProfile()
  const applications = useCustomerApplications()
  const { start: startApplication, starting, error } = useStartApplication()

  if (!productParam || !isCreditShowcaseProductKey(productParam)) {
    return <Navigate to={routes.credit.index} replace />
  }

  const profile = profileOf(user?.profile?.occupation)
  const product = creditProducts[productParam]
  const lock = lockOf({
    product: productParam,
    profile,
    currentSemester: user?.profile?.current_semester ?? null,
    graduationDate: user?.profile?.graduation_date ?? null,
  })

  if (
    !profileLoading &&
    (!productsFor(profile).includes(productParam) || lock)
  ) {
    return <Navigate to={routes.credit.index} replace />
  }

  const action = product.action
  const inProgress = applications.data?.in_progress[0]
  const blocked =
    action.kind === 'application' &&
    Boolean(inProgress) &&
    inProgress?.product !== action.product

  function start() {
    if (action.kind !== 'application' || blocked) return
    startApplication(action.product)
  }

  return (
    <>
      <PageHeader rollbackRoute={routes.credit.applications}>
        {product.name}
      </PageHeader>

      <Grid container spacing={2}>
        <Grid size={12}>
          <Paper
            sx={{
              p: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '48px 1fr', md: '56px 1fr' },
              gap: 2,
              alignItems: 'center',
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
            }}
          >
            <Box
              sx={{
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                borderRadius: 1.75,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(255,255,255,0.14)',
                color: 'secondary.main',
              }}
            >
              <product.icon />
            </Box>
            <Box>
              <Typography variant="h5">{product.name}</Typography>
              <Typography sx={{ opacity: 0.92, mt: 0.5 }}>
                {product.description}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {product.facts.map(([label, value]) => (
          <Grid key={label} size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography fontWeight={500} mt={0.5}>
                {value}
              </Typography>
            </Paper>
          </Grid>
        ))}

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" mb={1}>
              O que você vai precisar
            </Typography>
            <List disablePadding>
              {product.needs.map((need) => (
                <ListItem key={need} disableGutters sx={{ gap: 1.25 }}>
                  <CheckCircle color="secondary" fontSize="small" />
                  <Typography>{need}</Typography>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" mb={1}>
              Etapas da solicitação
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              {product.steps.map((step) => (
                <Typography key={step} component="li" sx={{ py: 0.5 }}>
                  {step}
                </Typography>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid size={12}>
          {blocked && inProgress ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '24px 1fr',
                gap: 1.25,
                p: 1.5,
                mb: 2,
                borderRadius: 1,
                bgcolor: 'info.light',
                color: 'info.contrastText',
              }}
            >
              <InfoOutlined />
              <Typography variant="body2">
                Você já tem uma solicitação em andamento:{' '}
                <b>{creditProducts[inProgress.product].name}</b>. Para abrir
                outra, conclua ou cancele a atual.
                {profile === 'doctor'
                  ? ' O Seguro RCP pode ser cotado a qualquer momento.'
                  : ''}
              </Typography>
            </Box>
          ) : null}

          <Paper
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {blocked
                ? 'Só é possível ter uma solicitação em andamento por vez.'
                : action.kind === 'application'
                  ? 'Você pode parar e continuar depois, de onde parou.'
                  : 'Você recebe a proposta antes de contratar.'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {action.kind === 'lead' ? (
                <Button
                  href={`${productPromoLinks.accountingSpecialist}&text=${encodeURIComponent(`Olá, quero saber mais sobre ${product.name}.`)}`}
                  target="_blank"
                  rel="noopener"
                >
                  Falar com especialista
                </Button>
              ) : blocked ? (
                <>
                  <Button
                    variant="text"
                    onClick={() => navigate(routes.credit.applications)}
                  >
                    Ver solicitação
                  </Button>
                  <Button disabled>Simule</Button>
                </>
              ) : action.kind === 'route' ? (
                <Button onClick={() => navigate(action.path)}>
                  Fazer cotação
                </Button>
              ) : (
                <LoadingButton loading={starting} onClick={start}>
                  Simule
                </LoadingButton>
              )}
            </Box>
          </Paper>

          {error && (
            <Typography color="error" variant="body2" mt={1}>
              {error}
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  )
}
